// strafe.games — shared account / credits / entitlements client (ECOSYSTEM CORE).
//
// One wallet, one set of unlocks, one progress store, shared by EVERY game on
// the strafe.games origin. This is the single source of truth on the client;
// it is the SAME public API the server backend exposes, so the swap to a
// server-authoritative economy touches only this file — never game code.
//
// CANONICAL COPY. This exact file is mirrored verbatim into every game's src/
// (games share no build, so the bytes are duplicated on purpose). Only edit the
// canonical copy, then re-sync.
//
// ── SERVER-AWARE (additive, dormant-safe) ────────────────────────────────────
// This SDK now has TWO modes, selected once at boot:
//   * mode='anon'     — the local-first economy below (localStorage). This is
//                       the ONLY mode unless `VITE_FEATURE_AUTH === 'true'`. In
//                       this mode the SDK issues ZERO network calls and behaves
//                       BYTE-IDENTICALLY to the original local-only SDK. SACRED.
//   * mode='signedIn' — server is the source of truth. The same public methods
//                       read a server-hydrated cache (balance / entitlements /
//                       god-mode) and mutations optimistically update + POST.
// The PUBLIC API (every method's name, signature, and sync/async shape) is
// IDENTICAL across both modes, so NO call site changes. Two purely-additive
// members are new: `Platform.refresh()` and `Platform.adsEnabled()`.
//
// EASY TO TUNE / SCALE: every limit + every game's gating lives in ONE served
// file, /platform-config.json (in hub/). Edit that one JSON to retune the whole
// site — no code changes, no per-game edits. The constants below are only the
// safe FALLBACK used if that file is missing/invalid, so a bad config can never
// break the site.
//
// Robustness guarantees of the local-first layer:
//   * Cross-tab/cross-game consistency — a `storage` listener invalidates the
//     cache + notifies whenever ANOTHER tab changes the wallet, so two open
//     games never show divergent balances.
//   * Atomic mutations — each change re-reads the latest value, recomputes, and
//     writes wallet + ledger together in one synchronous tick.
//   * Idempotency — one-off grants (welcome bonus, first-clear, purchases) are
//     keyed by a ref and never double-pay; daily caps bound ads + playtime.
//   * Integrity — corrupt/garbage values fall back to safe defaults; a schema
//     version guards future migrations; the balance is clamped to a finite int.
//   * History — a bounded ledger records what happened ("keep track").
//
// HONEST LIMIT (anon mode): localStorage is user-editable, so this layer is
// consistent + tamper-EVIDENT, not tamper-PROOF. True anti-abuse arrives with
// the server backend; signed-in mode is server-authoritative.

const NS = 'strafe.platform';
const SCHEMA_VERSION = 1;

const K = {
  schema: `${NS}.schema`,
  wallet: `${NS}.wallet.v1`,
  ent: `${NS}.entitlements.v1`,
  ledger: `${NS}.ledger.v1`,
  earned: `${NS}.earned.v1`,
  playtime: `${NS}.playtime.v1`,
  ads: `${NS}.ads.v1`,
  progress: `${NS}.progress.v1`,
  migrated: `${NS}.migrated.v1`,
} as const;

const OUR_KEYS: ReadonlySet<string> = new Set(Object.values(K));

// ---- environment / backend resolution ----------------------------------------

// Build-time literals (Vite-style env vars). AUTH gate is OFF unless explicitly
// flipped to the string 'true'; with it off this whole server layer is inert.
const AUTH_ON: boolean = import.meta.env.VITE_FEATURE_AUTH === 'true';

function resolveApiBase(): string {
  // PROD defaults to the API custom domain api.strafe.games. Frontend
  // (www.strafe.games) + API share the strafe.games eTLD+1, so requests are
  // SAME-SITE: the session cookie (Domain=.strafe.games; SameSite=Lax) is
  // first-party + carries across hub<->games, CORS allow-lists the page origins,
  // and the Worker sees the real client IP (rate limiting works). Override with
  // VITE_API_BASE_URL only for a custom backend.
  const raw =
    import.meta.env.VITE_API_BASE_URL
    ?? (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api.strafe.games');
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}
const API_BASE = resolveApiBase();

// ---- tunable economy config (overridable via /platform-config.json) ----------

export interface EconomyLimits {
  welcomeBonus: number;
  creditsPerClear: number;
  adReward: number;
  adDailyCap: number;
  playtimeCreditSeconds: number;
  playtimeDailyCap: number;
}

export interface ChapterConfig {
  sku: string;
  from: number; // first level index (0-based) this chapter gates
  price: number; // unlock price in credits
  title: string;
}

// A purchasable COSMETIC SKU (e.g. a knife / glove / weapon or viewmodel skin).
// Unlike a chapter it gates no level range — it is bought once as an entitlement
// and surfaced in the shared store. Used by cosmetics-only games (strafe / surf),
// whose gameplay is always free.
export interface CosmeticConfig {
  sku: string;
  price: number; // unlock price in credits
  title: string;
}

export interface GameConfig {
  freeLevels: number;
  chapters: ChapterConfig[];
  // OPTIONAL cosmetic SKUs for cosmetics-only games. Absent for level-pack
  // games. Surfaced in the shared store alongside chapters by storeItems().
  cosmetics?: CosmeticConfig[];
}

const DEFAULT_LIMITS: EconomyLimits = {
  welcomeBonus: 10,
  creditsPerClear: 2,
  adReward: 5,
  adDailyCap: 3,
  playtimeCreditSeconds: 180, // +1 credit per 3 min of active play
  playtimeDailyCap: 8,
};

// Live limits — MUTATED in place by loadConfig so any captured `Platform.limits`
// reference stays valid after a config load.
const limits: EconomyLimits = { ...DEFAULT_LIMITS };
const gameConfigs: Record<string, GameConfig> = {};

export interface PlatformFlags {
  // MASTER SWITCH. While true, the credit system runs in the background but
  // NOTHING is gated — every game treats all content as unlocked + free. Flip
  // to false (in /platform-config.json) to turn paywalls/credits on site-wide.
  unlockAll: boolean;
}

// Gate by DEFAULT. The site has LAUNCHED with the economy on (the served
// /platform-config.json sets unlockAll:false), so the safe pre-config default —
// used until that file loads, or if it ever fails to load — is to GATE: paid
// content stays locked while the free tier stays reachable (each game's
// progression FALLBACK supplies the free-count), and a config that sets
// unlockAll:true (the dormant posture) still unlocks everything once it loads.
// This also closes the boot race where a menu built before the config arrived
// would briefly read everything as free.
const flags: PlatformFlags = { unlockAll: false };

// Cloudflare Turnstile site key for the register form. Empty => DORMANT (no
// widget rendered, no token sent — byte-identical to today). Overridable via
// /platform-config.json's `turnstileSiteKey`. MUTATED in place by loadConfig.
let turnstileSiteKey = '';

const LEDGER_MAX = 200; // bounded history (newest kept)

export type UnlockResult = 'owned' | 'purchased' | 'insufficient';

export interface LedgerEntry {
  t: number; // timestamp (ms)
  d: number; // delta (+earn / -spend)
  r: string; // reason
  ref?: string; // idempotency / external ref
}

type Listener = () => void;
const listeners = new Set<Listener>();

const hasDOM = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

// ---- storage helpers ---------------------------------------------------------

function read<T>(key: string, fallback: T): T {
  if (!hasDOM) return fallback;
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  if (!hasDOM) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — non-fatal, the economy just won't persist */
  }
}

function notify(): void {
  // Iterate a SNAPSHOT, not the live Set. A listener may re-subscribe from
  // inside its own callback (a screen that rebuilds itself mounts a replacement
  // which subscribes again) — and a Set iterator visits entries appended during
  // iteration, so the live loop would re-enter forever and pin the main thread.
  // The `has` re-check keeps a listener that unsubscribed earlier in this same
  // fan-out from still being called after it's gone.
  for (const l of [...listeners]) if (listeners.has(l)) l();
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function posInt(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.round(v) : fallback;
}

// ---- server cache + transport ------------------------------------------------

type Mode = 'anon' | 'signedIn';
let mode: Mode = 'anon';
let userId: string | null = null;

// Server-hydrated state (signed-in only). `null` balance means "not yet loaded"
// → balance() falls back to local while a refresh is in flight.
let serverBalance: number | null = null;
let serverGodMode = false;
let serverEnt = new Set<string>(); // 'gameId:sku', cross-game (NO filter)

let initPromise: Promise<void> | null = null; // in-flight guard (idempotent init)

/** Cookie-auth fetch. Returns parsed JSON, or `null` on ANY failure (non-ok,
 *  throw, parse error). NEVER throws. When AUTH is off, a no-op that returns
 *  null without touching the network — this is what keeps anon mode dormant. */
async function api<T>(path: string, init?: RequestInit): Promise<T | null> {
  if (!AUTH_ON) return null;
  if (typeof fetch === 'undefined') return null;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

// ---- config loading ----------------------------------------------------------

function isChapterConfig(c: unknown): c is ChapterConfig {
  if (typeof c !== 'object' || c === null) return false;
  const o = c as Record<string, unknown>;
  return (
    typeof o.sku === 'string' &&
    typeof o.from === 'number' &&
    typeof o.price === 'number' &&
    typeof o.title === 'string'
  );
}

function isCosmeticConfig(c: unknown): c is CosmeticConfig {
  if (typeof c !== 'object' || c === null) return false;
  const o = c as Record<string, unknown>;
  return typeof o.sku === 'string' && typeof o.price === 'number' && typeof o.title === 'string';
}

function isGameConfig(c: unknown): c is GameConfig {
  if (typeof c !== 'object' || c === null) return false;
  const o = c as Record<string, unknown>;
  if (typeof o.freeLevels !== 'number' || !Array.isArray(o.chapters) || !o.chapters.every(isChapterConfig)) {
    return false;
  }
  if (o.cosmetics !== undefined && (!Array.isArray(o.cosmetics) || !o.cosmetics.every(isCosmeticConfig))) {
    return false;
  }
  return true;
}

/** Fetch /platform-config.json (same origin) and override the baked defaults.
 *  Safe to call once at game boot; on any failure the defaults stand. Kicks the
 *  server session bootstrap (`init()`) at the very end (no-op when AUTH off). */
async function loadConfig(url = '/platform-config.json'): Promise<void> {
  if (!hasDOM) return;
  try {
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-cache' });
    if (!res.ok) return;
    const cfg = (await res.json()) as {
      unlockAll?: unknown;
      turnstileSiteKey?: unknown;
      limits?: Partial<Record<keyof EconomyLimits, unknown>>;
      games?: Record<string, unknown>;
    };
    if (typeof cfg.unlockAll === 'boolean') {
      flags.unlockAll = cfg.unlockAll;
    }
    if (typeof cfg.turnstileSiteKey === 'string') {
      turnstileSiteKey = cfg.turnstileSiteKey;
    }
    if (cfg.limits) {
      const L = cfg.limits;
      limits.welcomeBonus = posInt(L.welcomeBonus, limits.welcomeBonus);
      limits.creditsPerClear = posInt(L.creditsPerClear, limits.creditsPerClear);
      limits.adReward = posInt(L.adReward, limits.adReward);
      limits.adDailyCap = posInt(L.adDailyCap, limits.adDailyCap);
      limits.playtimeCreditSeconds = posInt(L.playtimeCreditSeconds, limits.playtimeCreditSeconds);
      limits.playtimeDailyCap = posInt(L.playtimeDailyCap, limits.playtimeDailyCap);
    }
    if (cfg.games && typeof cfg.games === 'object') {
      for (const [g, c] of Object.entries(cfg.games)) {
        if (isGameConfig(c)) gameConfigs[g] = c;
      }
    }
    notify();
  } catch {
    /* keep baked defaults */
  } finally {
    void init();
  }
}

/** A game's gating config from the central file, or the caller's fallback. */
function gameConfig(gameId: string, fallback: GameConfig): GameConfig {
  return gameConfigs[gameId] ?? fallback;
}

// ---- schema guard ------------------------------------------------------------

function initSchema(): void {
  if (!hasDOM) return;
  try {
    const v = Number(localStorage.getItem(K.schema) ?? '0');
    if (v !== SCHEMA_VERSION) localStorage.setItem(K.schema, String(SCHEMA_VERSION));
  } catch {
    /* ignore */
  }
}

// ---- wallet (cached; cache invalidated on cross-tab writes) ------------------

let cachedBalance: number | null = null;

function readWalletFresh(): number {
  const w = read<{ credits: unknown }>(K.wallet, { credits: 0 });
  const c = typeof w.credits === 'number' && Number.isFinite(w.credits) ? w.credits : 0;
  return Math.max(0, Math.round(c));
}

function localBalance(): number {
  if (cachedBalance === null) cachedBalance = readWalletFresh();
  return cachedBalance;
}

/** Sync balance. Signed-in → server cache (falls back to local until hydrated);
 *  anon → local wallet. */
function getBalance(): number {
  if (mode === 'signedIn') return serverBalance ?? localBalance();
  return localBalance();
}

// ---- ledger (bounded history) ------------------------------------------------

function appendLedger(entry: LedgerEntry): void {
  const log = read<LedgerEntry[]>(K.ledger, []);
  log.push(entry);
  if (log.length > LEDGER_MAX) log.splice(0, log.length - LEDGER_MAX);
  write(K.ledger, log);
}

function getLedger(): LedgerEntry[] {
  return read<LedgerEntry[]>(K.ledger, []);
}

// ---- idempotency set ---------------------------------------------------------

function earnedSet(): Set<string> {
  return new Set(read<string[]>(K.earned, []));
}

// ---- core mutation (atomic, idempotent) — LOCAL ------------------------------

function transact(delta: number, reason: string, ref?: string): boolean {
  // Anonymous (no-account) users can NEVER gain credits. Credits are an
  // account-only feature; granting them anonymously is a bypass — clear
  // localStorage / open an incognito window → fresh credits → unlock a chapter
  // → repeat, one level at a time. Registered users get ALL credits decided
  // server-side (welcome bonus on signup, earn/playtime via the API), so the
  // signed-in path never reaches this local mutator for a positive delta. We
  // gate at this single chokepoint so no earning path (welcome/clear/playtime/
  // any future one) can leak credits to anon. Spends (delta <= 0) are untouched.
  if (delta > 0 && mode !== 'signedIn') return false;
  if (ref !== undefined) {
    const earned = earnedSet();
    if (earned.has(ref)) return false;
    earned.add(ref);
    write(K.earned, [...earned]);
  }
  const next = Math.max(0, Math.round(readWalletFresh() + delta));
  write(K.wallet, { credits: next });
  appendLedger({ t: Date.now(), d: delta, r: reason, ref });
  cachedBalance = next;
  notify();
  return true;
}

// ---- entitlements ------------------------------------------------------------

function entitlementSet(): Set<string> {
  return new Set(read<string[]>(K.ent, []));
}

/** Signed-in → server god-mode OR server cache; anon → local set. */
function hasEntitlement(fullSku: string): boolean {
  if (mode === 'signedIn') return serverGodMode || serverEnt.has(fullSku);
  return entitlementSet().has(fullSku);
}

function grantEntitlementLocal(fullSku: string): void {
  const set = entitlementSet();
  if (set.has(fullSku)) return;
  set.add(fullSku);
  write(K.ent, [...set]);
  notify();
}

/** Signed-in → server cache filtered by `gameId:` prefix; anon → local. */
function entitlementsFor(gameId: string): string[] {
  const prefix = `${gameId}:`;
  const src = mode === 'signedIn' ? serverEnt : entitlementSet();
  return [...src].filter((s) => s.startsWith(prefix)).map((s) => s.slice(prefix.length));
}

// ---- server session lifecycle ------------------------------------------------

interface MeBody {
  userId?: unknown;
}
interface WalletBody {
  balance?: unknown;
  role?: unknown;
  isAdmin?: unknown;
}
interface EntItem {
  gameId?: unknown;
  sku?: unknown;
}
interface EntBody {
  godMode?: unknown;
  items?: unknown;
}

/** Boot the server session. AUTH off → stays anon, ZERO network. AUTH on →
 *  GET /api/me: a userId means signed-in (hydrate + migrate + notify), null
 *  means anon. Idempotent via an in-flight promise guard. */
function init(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    if (!AUTH_ON) {
      mode = 'anon';
      return;
    }
    const me = await api<MeBody>('/api/me', { method: 'GET' });
    const uid = me && typeof me.userId === 'string' && me.userId.length > 0 ? me.userId : null;
    if (uid === null) {
      mode = 'anon';
      return;
    }
    mode = 'signedIn';
    userId = uid;
    await refresh();
    await maybeMigrate();
    notify();
  })();
  return initPromise;
}

/** Re-pull the server wallet + entitlements (signed-in only). Caches ALL
 *  entitlements cross-game (NO gameId filter). Notifies on any change. */
async function refresh(): Promise<void> {
  if (mode !== 'signedIn') return;
  const [wallet, ent] = await Promise.all([
    api<WalletBody>('/api/wallet', { method: 'GET' }),
    api<EntBody>('/api/entitlements', { method: 'GET' }),
  ]);
  let changed = false;
  if (wallet) {
    const bal = typeof wallet.balance === 'number' && Number.isFinite(wallet.balance)
      ? Math.max(0, Math.round(wallet.balance))
      : 0;
    if (bal !== serverBalance) {
      serverBalance = bal;
      changed = true;
    }
  }
  if (ent) {
    const god = ent.godMode === true;
    if (god !== serverGodMode) {
      serverGodMode = god;
      changed = true;
    }
    const next = new Set<string>();
    if (Array.isArray(ent.items)) {
      for (const raw of ent.items as EntItem[]) {
        if (raw && typeof raw.gameId === 'string' && typeof raw.sku === 'string') {
          next.add(`${raw.gameId}:${raw.sku}`);
        }
      }
    }
    if (!setsEqual(next, serverEnt)) {
      serverEnt = next;
      changed = true;
    }
  }
  if (changed) notify();
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

// ---- earning -----------------------------------------------------------------

/** Signed-in → the SERVER grants the welcome bonus on signup; we just refresh.
 *  Anon (no account) → NO welcome bonus: the transact() chokepoint blocks anon
 *  credit gains (credits are account-only, to prevent the storage-reset bypass).
 *  The call is left in place so signed-in still refreshes; it no-ops for anon. */
function grantWelcomeBonusOnce(): void {
  if (mode === 'signedIn') {
    void refresh();
    return;
  }
  transact(limits.welcomeBonus, 'welcome_bonus', 'welcome_bonus');
}

interface EarnBody {
  granted?: unknown;
  balance?: unknown;
}

/** Anon → local idempotent transact. Signed-in → idempotent in the local
 *  earned-set for instant UI, then POST /api/economy/earn (the SERVER decides
 *  the amount; the client `amount` is ignored when signed-in). `eventId` is
 *  '<gameId>:<...>' so gameId = eventId.split(':')[0]. */
function earnOnce(eventId: string, amount: number): boolean {
  if (mode === 'signedIn') {
    const earned = earnedSet();
    if (earned.has(eventId)) return false;
    earned.add(eventId);
    write(K.earned, [...earned]);
    const gameId = eventId.split(':')[0] ?? '';
    void (async () => {
      const res = await api<EarnBody>('/api/economy/earn', {
        method: 'POST',
        body: JSON.stringify({ gameId, eventId }),
      });
      if (res && typeof res.balance === 'number' && Number.isFinite(res.balance)) {
        serverBalance = Math.max(0, Math.round(res.balance));
        notify();
      }
    })();
    return true;
  }
  return transact(amount, 'earn', eventId);
}

interface DailyState {
  day: string;
  acc: number;
  creditedToday: number;
}

interface PlaytimeBody {
  granted?: unknown;
  balance?: unknown;
}

/** Sync, returns credits granted THIS call (always 0 when signed-in, since the
 *  server decides asynchronously). Anon → local capped accrual. Signed-in →
 *  accumulate locally and, when the accumulator crosses the per-credit window,
 *  fire-and-forget POST /api/economy/playtime; the server reply updates the
 *  cached balance + appends a local ledger note. */
function tickPlaytime(seconds: number): number {
  if (!(seconds > 0)) return 0;
  if (mode === 'signedIn') {
    const st = read<DailyState>(K.playtime, { day: todayKey(), acc: 0, creditedToday: 0 });
    if (st.day !== todayKey()) {
      st.day = todayKey();
      st.acc = 0;
      st.creditedToday = 0;
    }
    st.acc += seconds;
    if (st.acc >= limits.playtimeCreditSeconds) {
      st.acc -= limits.playtimeCreditSeconds; // claim one window; server is authoritative on the grant
      write(K.playtime, st);
      void (async () => {
        const res = await api<PlaytimeBody>('/api/economy/playtime', { method: 'POST' });
        if (res && typeof res.granted === 'number' && res.granted > 0) {
          if (typeof res.balance === 'number' && Number.isFinite(res.balance)) {
            serverBalance = Math.max(0, Math.round(res.balance));
          }
          appendLedger({ t: Date.now(), d: res.granted, r: 'playtime' });
          notify();
        }
      })();
    } else {
      write(K.playtime, st);
    }
    return 0;
  }

  const st = read<DailyState>(K.playtime, { day: todayKey(), acc: 0, creditedToday: 0 });
  if (st.day !== todayKey()) {
    st.day = todayKey();
    st.acc = 0;
    st.creditedToday = 0;
  }
  st.acc += seconds;
  let granted = 0;
  while (st.acc >= limits.playtimeCreditSeconds && st.creditedToday < limits.playtimeDailyCap) {
    st.acc -= limits.playtimeCreditSeconds;
    st.creditedToday += 1;
    granted += 1;
  }
  write(K.playtime, st);
  if (granted > 0) transact(granted, 'playtime');
  return granted;
}

function adsRemainingToday(): number {
  const st = read<{ day: string; count: number }>(K.ads, { day: todayKey(), count: 0 });
  if (st.day !== todayKey()) return limits.adDailyCap;
  return Math.max(0, limits.adDailyCap - st.count);
}

/** No-op in ALL modes. A browser cannot mint ad credits — that would be a
 *  trivially-forged client grant. The REAL rewarded-ad path is server-side:
 *  ad network → POST /api/ads/ssv (HMAC-signed server-to-server verification)
 *  → server mints the reward → client picks it up on the next refresh(). This
 *  method is kept for API stability; games should hide the affordance via
 *  adsEnabled() (false today) rather than calling this. */
function watchAdReward(): number {
  return 0;
}

/** Whether the rewarded-ad affordance should be shown. False until a real ad
 *  network + the SSV mint path are wired; games key the 'watch ad' UI off this. */
function adsEnabled(): boolean {
  return false;
}

// ---- spending ----------------------------------------------------------------

interface PurchaseBody {
  outcome?: unknown;
  balance?: unknown;
  price?: unknown;
}

/** Server reconciliation for a signed-in optimistic unlock. Reconciles the
 *  cached balance from the response; on 'insufficient' rolls back the optimistic
 *  entitlement add; always refreshes at the end so the cache is authoritative. */
async function purchaseOnServer(gameId: string, sku: string, fullSku: string): Promise<void> {
  const res = await api<PurchaseBody>('/api/entitlements/purchase', {
    method: 'POST',
    body: JSON.stringify({ gameId, sku }),
  });
  if (res) {
    if (typeof res.balance === 'number' && Number.isFinite(res.balance)) {
      serverBalance = Math.max(0, Math.round(res.balance));
    }
    if (res.outcome === 'insufficient') {
      serverEnt.delete(fullSku); // roll back the optimistic grant
    }
    notify();
  }
  await refresh();
}

/** Sync unlock. Anon → local spend. Signed-in → owned/god short-circuit, then
 *  affordability check against the cache, then OPTIMISTIC apply (add ent, debit
 *  cache, notify) + fire purchaseOnServer to reconcile. Returns the optimistic
 *  outcome immediately (no call-site await change). */
function unlock(fullSku: string, priceCredits: number): UnlockResult {
  if (mode === 'signedIn') {
    if (serverGodMode || serverEnt.has(fullSku)) return 'owned';
    if ((serverBalance ?? 0) < priceCredits) return 'insufficient';
    const colon = fullSku.indexOf(':');
    const gameId = colon >= 0 ? fullSku.slice(0, colon) : '';
    const sku = colon >= 0 ? fullSku.slice(colon + 1) : fullSku;
    serverEnt.add(fullSku);
    serverBalance = Math.max(0, (serverBalance ?? 0) - priceCredits);
    notify();
    void purchaseOnServer(gameId, sku, fullSku);
    return 'purchased';
  }

  if (entitlementSet().has(fullSku)) return 'owned';
  if (readWalletFresh() < priceCredits) return 'insufficient';
  transact(-priceCredits, 'unlock', undefined);
  grantEntitlementLocal(fullSku);
  return 'purchased';
}

// ---- one-time local→server migration -----------------------------------------

interface MigrateEnt {
  gameId: string;
  sku: string;
}

/** Idempotent per user. On first sign-in for a given userId, hand the local
 *  wallet + local entitlements to the server (which is idempotent per
 *  'migrate:<userId>'), then ZERO OUT the local economy so it never double-
 *  counts on a later anon session. Guarded by a localStorage array of migrated
 *  userIds; on server failure it does nothing (safe to retry next sign-in). */
async function maybeMigrate(): Promise<void> {
  if (mode !== 'signedIn' || userId === null) return;
  const done = read<string[]>(K.migrated, []);
  if (done.includes(userId)) return;

  const credits = readWalletFresh();
  const entitlements: MigrateEnt[] = [...entitlementSet()].map((full) => {
    const colon = full.indexOf(':');
    return colon >= 0
      ? { gameId: full.slice(0, colon), sku: full.slice(colon + 1) }
      : { gameId: '', sku: full };
  });

  const res = await api<{ ok?: unknown; balance?: unknown }>('/api/economy/migrate', {
    method: 'POST',
    body: JSON.stringify({ credits, entitlements }),
  });
  if (res === null) return; // failure → retry next sign-in (server ref makes it safe)

  done.push(userId);
  write(K.migrated, done);
  write(K.wallet, { credits: 0 });
  cachedBalance = 0;
  write(K.ent, []);
  appendLedger({ t: Date.now(), d: -credits, r: 'migrated_to_account' });
  await refresh();
}

// ---- store (derived from loaded config; cross-game) --------------------------

/** A purchasable chapter/item, flattened across EVERY game in the loaded
 *  config. `owned` reflects the current wallet/entitlement state (god-mode +
 *  server cache when signed in, local set when anon). Empty array when no
 *  config has loaded yet (or no game declares chapters). Derived live, so it
 *  reflects config + ownership at call time. Never throws. */
export interface StoreItem {
  gameId: string;
  sku: string;
  price: number;
  title: string;
  owned: boolean;
}

function storeItems(): StoreItem[] {
  const out: StoreItem[] = [];
  for (const [gameId, cfg] of Object.entries(gameConfigs)) {
    for (const ch of cfg.chapters) {
      out.push({
        gameId,
        sku: ch.sku,
        price: ch.price,
        title: ch.title,
        owned: hasEntitlement(`${gameId}:${ch.sku}`),
      });
    }
    for (const cos of cfg.cosmetics ?? []) {
      out.push({
        gameId,
        sku: cos.sku,
        price: cos.price,
        title: cos.title,
        owned: hasEntitlement(`${gameId}:${cos.sku}`),
      });
    }
  }
  return out;
}

// ---- per-game progress ("keep track of what you're doing") -------------------
// Progress stays LOCAL-ONLY in both modes (server progress is deferred).

function getProgress<T>(gameId: string, fallback: T): T {
  const all = read<Record<string, unknown>>(K.progress, {});
  return Object.prototype.hasOwnProperty.call(all, gameId) ? (all[gameId] as T) : fallback;
}

function saveProgress(gameId: string, data: unknown): void {
  const all = read<Record<string, unknown>>(K.progress, {});
  all[gameId] = data;
  write(K.progress, all);
  notify();
}

// ---- cross-tab / cross-game sync ---------------------------------------------

if (hasDOM) {
  initSchema();
  window.addEventListener('storage', (e) => {
    if (e.key === null || OUR_KEYS.has(e.key)) {
      cachedBalance = null;
      notify();
    }
  });
  // Optional: re-pull server truth when the tab becomes visible again (no-op in
  // anon mode — refresh() early-returns when not signed in).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && mode === 'signedIn') void refresh();
  });
}

// ---- public API --------------------------------------------------------------

export const Platform = {
  /** Subscribe to wallet/entitlement/progress/config changes; returns unsubscribe. */
  onChange(cb: Listener): () => void {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  },
  /** Load central tunables from /platform-config.json (call once at boot). Also
   *  kicks the server session bootstrap (no-op when AUTH is off). */
  loadConfig,
  /** Re-pull server wallet + entitlements (no-op in anon mode). Additive. */
  refresh,
  /** Live, override-able economy limits (read-only intent). */
  limits: limits as Readonly<EconomyLimits>,
  /** Live feature flags. `flags.unlockAll` true => nothing is gated. */
  flags: flags as Readonly<PlatformFlags>,
  /** Cloudflare Turnstile site key for the register form ('' => dormant: no
   *  widget, no token). Sourced from /platform-config.json's `turnstileSiteKey`. */
  turnstileSiteKey(): string {
    return turnstileSiteKey;
  },
  /** A game's gating config (central file, else the provided fallback). */
  gameConfig,
  balance: getBalance,
  hasEntitlement,
  entitlementsFor,
  unlock,
  grantWelcomeBonusOnce,
  earnOnce,
  tickPlaytime,
  watchAdReward,
  /** Whether the rewarded-ad affordance should render (false until SSV wired). Additive. */
  adsEnabled,
  adsRemainingToday,
  getProgress,
  saveProgress,
  getLedger,
  /** Purchasable items across ALL games in the loaded config (flattened
   *  chapters → {gameId, sku, price, title, owned}). Empty until config loads. */
  storeItems,
};
